import React, { useState, useMemo } from "react";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Icon
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon, EditIcon, CheckIcon } from "@chakra-ui/icons";
import { AggregationType, ColumnType } from "build-ts/enums";
import { DatePeriodEnum, datePeriodEnumToJSON, GrowthBenchmarkProto, GrowthBenchmarkType } from "build-ts/relative_date";
import { ObservationColumnProto, MetricProto } from "build-ts/metadata";
import { AvailableNode, PropagationItem, StepInterface } from "./Interface";
import { getKGGrowthBenchmarks } from "utils/analysisUtils";
import { TbFilterBolt } from "react-icons/tb";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import PropagationModal from "containers/analysis-plan/PropagationModal";

interface BenchmarksProps {
  currentNode: StepInterface;
  selectedBenchmarks: BenchmarkEntityProto[];
  setSelectedBenchmarks: React.Dispatch<React.SetStateAction<BenchmarkEntityProto[]>>;
  benchmarkColumnHashes: ColumnHashProto[];
  setBenchmarkColumnHashes: React.Dispatch<React.SetStateAction<ColumnHashProto[]>>;
  selectedColumns: ObservationColumnProto[];
}


const Benchmarks = ({ 
  currentNode,
  selectedBenchmarks, 
  setSelectedBenchmarks, 
  benchmarkColumnHashes,
  setBenchmarkColumnHashes,
  selectedColumns = []
}: BenchmarksProps) => {
  // Get context functions
  const { nodeMap, updateNode } = useAnalysisPlanContext();
  // State for the current benchmark selection
  const [currentSelection, setCurrentSelection] = useState<BenchmarkEntityProto | null>(null);
  // State for selected metrics for the current benchmark
  const [selectedMetricsForBenchmark, setSelectedMetricsForBenchmark] = useState<MetricProto[]>([]);
  // Derive measure columns from selectedColumns
  const selectedMeasureColumns = useMemo(() =>
    (selectedColumns || []).filter(column => column.kg_column_header?.column_type === ColumnType.MEASURE),
    [selectedColumns]
  );
  // State for Last N Period Aggregation
  const [numberOfPeriods, setNumberOfPeriods] = useState<number>(4);
  // State for Generalized Growth Benchmark
  const [shiftPeriod, setShiftPeriod] = useState<DatePeriodEnum>(DatePeriodEnum.MONTH);
  const [generalizedNumberOfPeriods, setGeneralizedNumberOfPeriods] = useState<number>(1);
  const [generalizedAggregationType, setGeneralizedAggregationType] = useState<AggregationType>(AggregationType.AVERAGE);
  // State for custom names by metric/measure ID
  const [customNamesByActualsId, setCustomNamesByActualsId] = useState<Record<string, string>>({});
  
  // State for propagation modal
  const [propagationModal, setPropagationModal] = useState<{
    isOpen: boolean;
    item: PropagationItem | null;
    availableNodes: AvailableNode[];
    shouldOverwrite: boolean;
  }>({
    isOpen: false,
    item: null,
    availableNodes: [],
    shouldOverwrite: true
  });

  // State for editing individual benchmark column names
  const [editingKey, setEditingKey] = useState<string>("");
  const [editingColumnName, setEditingColumnName] = useState<string>("");

  const handlePropagateBenchmarkClick = (benchmark: BenchmarkEntityProto | undefined, currentNodeId: string, hash: ColumnHashProto) => {
    if (!benchmark) return;
    const allNodeIds = Array.from(nodeMap.keys()).filter(id => id !== currentNodeId);
    const allNodes = allNodeIds.map((id) => {
      const node = nodeMap.get(id);
      return {
        id,
        name: node?.label || `Node ${id}`,
        selected: true,
      };
    });

    setPropagationModal({
      isOpen: true,
      item: {
        type: "benchmark",
        benchmark: benchmark,
        benchmarkColumnHashes: [hash]
      },
      availableNodes: allNodes,
      shouldOverwrite: true
    });
  };

 // Handle propagation modal node toggle
  const handlePropagationNodeToggle = (nodeId: string) => {
    setPropagationModal(prev => ({
      ...prev,
      availableNodes: prev.availableNodes.map(node => 
        node.id === nodeId 
          ? { ...node, selected: !node.selected }
          : node
      )
    }));
  };

  // Handle propagation modal overwrite toggle
  const handlePropagationOverwriteToggle = (shouldOverwrite: boolean) => {
    setPropagationModal(prev => ({
      ...prev,
      shouldOverwrite
    }));
  };

  // Function to confirm benchmark propagation to selected descendants
  const handleConfirmPropagation = () => {
    if (!propagationModal.item || propagationModal.item.type !== "benchmark" || !propagationModal.item.benchmark || !currentNode) return;


    const benchmarkToPropagate = propagationModal.item.benchmark;


    // Prefer benchmarkColumnHashes supplied in the modal (for single-hash propagation), otherwise derive from node
    const relatedBenchmarkColumnHashes: ColumnHashProto[] = (currentNode.benchmark_column_hashes?.filter(
          (hash) => hash.benchmark_id === benchmarkToPropagate.header?.id
        ) || []);

    // Only use selected descendants for propagation
    const selectedDescendantIds = propagationModal.availableNodes
      .filter(node => node.selected)
      .map(node => node.id);

    selectedDescendantIds.forEach(descendantId => {
      const descendantNode = nodeMap?.get(descendantId);
      if (descendantNode) {
        // Handle benchmark propagation
        if (propagationModal.shouldOverwrite) {
          const existingBenchmarkIndex = descendantNode.selected_benchmarks?.findIndex(
            existingBenchmark => existingBenchmark.header?.id === benchmarkToPropagate.header?.id
          );
          if (existingBenchmarkIndex !== undefined && existingBenchmarkIndex >= 0) {
            descendantNode.selected_benchmarks?.splice(existingBenchmarkIndex, 1);
          }
          
          // Also remove existing benchmark column hashes for this benchmark
          descendantNode.benchmark_column_hashes = descendantNode.benchmark_column_hashes?.filter(
            hash => hash.benchmark_id !== benchmarkToPropagate.header?.id
          ) || [];
        }
        
        // Add the benchmark
        descendantNode.selected_benchmarks?.push(benchmarkToPropagate);
        
        // Add related benchmark column hashes, but only for metrics/columns that exist in the target node
        const targetColumnIds = new Set<string>();
        
        // Collect all available column IDs in the target node
        descendantNode.selected_metrics?.forEach(metric => {
          if (metric.header?.id) targetColumnIds.add(metric.header.id);
        });
        descendantNode.selected_columns?.forEach(column => {
          if (column.column_id && 
              column.kg_column_header?.column_type !== ColumnType.ATTRIBUTE &&
              column.kg_column_header?.column_type !== ColumnType.TEMPORAL) {
            targetColumnIds.add(column.column_id);
          }
        });
        
        // Add benchmark column hashes for matching actuals_ids
        relatedBenchmarkColumnHashes.forEach(benchmarkHash => {
          if (benchmarkHash.actuals_id && targetColumnIds.has(benchmarkHash.actuals_id)) {
            // Check if this exact benchmark column hash already exists
            const exists = descendantNode.benchmark_column_hashes?.some(existing =>
              existing.actuals_id === benchmarkHash.actuals_id &&
              existing.benchmark_id === benchmarkHash.benchmark_id
            );
            
            if (!exists) {
              descendantNode.benchmark_column_hashes?.push({
                actuals_id: benchmarkHash.actuals_id,
                benchmark_id: benchmarkHash.benchmark_id,
                is_material: benchmarkHash.is_material || false,
                column_name: benchmarkHash.column_name
              });
            }
          }
        });
        
        updateNode(descendantNode);
      }
    });

    // Close modal and reset state
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Close propagation modal
  const handleClosePropagation = () => {
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Check if current selection is a Last N Period Aggregation benchmark
  const isLastNPeriodAggregation = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("Last N") && benchmarkName.includes("Average")
    );
  }, [currentSelection]);

  // Check if current selection is a simple Last X benchmark (LAST_DAY, LAST_WEEK, etc.)
  const isSimpleLastBenchmark = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.startsWith("Last ") && !benchmarkName.includes("Last N") && !benchmarkName.includes("Average")
    );
  }, [currentSelection]);

  // Check if current selection is a Generalized Growth Benchmark (old style - should be hidden now)
  const isGeneralizedGrowthBenchmark = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName === "Generalized Growth Benchmark"
    );
  }, [currentSelection]);

  // Check if current selection is a N Period Ago benchmark (without aggregation)
  const isNPeriodAgo = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("N ") && benchmarkName.includes(" Ago") &&
      !currentSelection.growth_benchmark.aggregation_type
    );
  }, [currentSelection]);

  // Check if there are any date filters in the current node
  const hasDateFilters = useMemo(() => {
    if (!currentNode?.selected_filters) return false;
    return currentNode.selected_filters.some(filter =>
      filter.date_filter !== undefined && filter.date_filter !== null
    );
  }, [currentNode?.selected_filters]);


  // Filter benchmarks to only show growth benchmarks when date filters are present
  const availableBenchmarks = useMemo(() => {
    if (!currentNode?.benchmarks) return [];
    
    // Get regular benchmarks (non-growth)
    const regularBenchmarks = currentNode.benchmarks.filter(benchmark =>
      !benchmark.growth_benchmark ||
      benchmark.growth_benchmark.growth_type === undefined
    );
    
    if (!hasDateFilters) {
      // If no date filters, only show regular benchmarks
      return regularBenchmarks;
    }
    
    // If date filters are present, generate dynamic growth benchmarks
    const dynamicGrowthBenchmarks = getKGGrowthBenchmarks(currentNode.selected_filters);
    
    // Combine regular benchmarks with dynamically generated growth benchmarks
    return [...regularBenchmarks, ...dynamicGrowthBenchmarks];
  }, [currentNode?.benchmarks, currentNode?.selected_filters, hasDateFilters]);

  // Handle benchmark selection
  const handleBenchmarkSelect = (benchmark: BenchmarkEntityProto) => {
    // Check if benchmark is already selected
    const isAlreadySelected = selectedBenchmarks.some(
      (selected) => selected.header?.id === benchmark.header?.id
    );

    if (isAlreadySelected) {
      // Show the benchmark selection but don't add it again
      setCurrentSelection(benchmark);
    } else {
      // Set as current selection
      setCurrentSelection(benchmark);
    }
    // Reset selected metrics for this benchmark and select all by default
    setSelectedMetricsForBenchmark(currentNode?.selected_metrics || []);
    
    // If it's a Last N Period Aggregation, set the default values
    const benchmarkName = benchmark.header?.name || "";
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("Last N") && benchmarkName.includes("Average")
    ) {
      if (benchmark.growth_benchmark.number) {
        setNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      // Aggregation type is fixed to AVERAGE for Last N benchmarks
    }

    // If it's a simple Last X benchmark, no configuration needed (fixed values)
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.startsWith("Last ") && !benchmarkName.includes("Last N") && !benchmarkName.includes("Average")
    ) {
      // No configuration needed - these have fixed values
    }

    // If it's a Generalized Growth Benchmark (old style), set the default values
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName === "Generalized Growth Benchmark"
    ) {
      if (benchmark.growth_benchmark.number) {
        setGeneralizedNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      if (benchmark.growth_benchmark.aggregation_type) {
        setGeneralizedAggregationType(benchmark.growth_benchmark.aggregation_type);
      }
      if (benchmark.growth_benchmark.growth_period) {
        setShiftPeriod(benchmark.growth_benchmark.growth_period);
      }
    }

    // If it's a N Period Ago benchmark, set the default values
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("N ") && benchmarkName.includes(" Ago") &&
      !benchmark.growth_benchmark.aggregation_type
    ) {
      if (benchmark.growth_benchmark.number) {
        setGeneralizedNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      if (benchmark.growth_benchmark.growth_period !== undefined) {
        setShiftPeriod(benchmark.growth_benchmark.growth_period);
      } else {
        if (benchmarkName.includes("Day Ago")) {
          setShiftPeriod(DatePeriodEnum.DAY);
        } else if (benchmarkName.includes("Week Ago")) {
          setShiftPeriod(DatePeriodEnum.WEEK);
        } else if (benchmarkName.includes("Month Ago")) {
          setShiftPeriod(DatePeriodEnum.MONTH);
        } else if (benchmarkName.includes("Quarter Ago")) {
          setShiftPeriod(DatePeriodEnum.QUARTER);
        } else if (benchmarkName.includes("Year Ago")) {
          setShiftPeriod(DatePeriodEnum.YEAR);
        }
      }
    }
  };

  // Handle metric toggle for benchmark
  const handleMetricToggleForBenchmark = (metric: MetricProto) => {
    setSelectedMetricsForBenchmark(prev => {
      const exists = prev.some(m => m.header?.id === metric.header?.id);
      if (exists) {
        return prev.filter(m => m.header?.id !== metric.header?.id);
      } else {
        return [...prev, metric];
      }
    });
  };

  // Handle Select All metrics toggle
  const handleSelectAllMetrics = () => {
    const allMetrics = currentNode?.selected_metrics || [];
    const isAllSelected = allMetrics.length > 0 && 
      allMetrics.every(metric => selectedMetricsForBenchmark.some(selected => selected.header?.id === metric.header?.id));
    
    if (isAllSelected) {
      // Deselect all
      setSelectedMetricsForBenchmark([]);
    } else {
      // Select all
      setSelectedMetricsForBenchmark(allMetrics);
    }
  };

  // Check if all metrics are selected
  const isAllMetricsSelected = useMemo(() => {
    const allMetrics = currentNode?.selected_metrics || [];
    return allMetrics.length > 0 && 
      allMetrics.every(metric => selectedMetricsForBenchmark.some(selected => selected.header?.id === metric.header?.id));
  }, [currentNode?.selected_metrics, selectedMetricsForBenchmark]);
  
  // Check if we can add a benchmark (need selection and metrics or measures)
  const canAddBenchmark = useMemo(() => {
    return currentSelection !== null && 
      (selectedMetricsForBenchmark.length > 0 || selectedMeasureColumns.length > 0);
  }, [currentSelection, selectedMetricsForBenchmark.length, selectedMeasureColumns.length]);

  // Add current selection to the list
  const handleAddSelection = () => {
    if (!currentSelection || (selectedMetricsForBenchmark.length === 0 && selectedMeasureColumns.length === 0)) return;

    // Check if benchmark is already in the selection
    const benchmarkExists = selectedBenchmarks.some(
      (b) => b.header?.id === currentSelection.header?.id
    );
    // Create a copy of the benchmark to modify if needed
    let benchmarkToAdd = { ...currentSelection };
    // If it's a Last N Period Aggregation, update its configuration
    if (isLastNPeriodAggregation && benchmarkToAdd.growth_benchmark) {
      const shiftPeriod = benchmarkToAdd.growth_benchmark.growth_period;
      let shiftPeriodName = "PERIOD";
      // Convert enum to proper string name
      switch (shiftPeriod) {
        case DatePeriodEnum.DAY:
          shiftPeriodName = "DAY";
          break;
        case DatePeriodEnum.WEEK:
          shiftPeriodName = "WEEK";
          break;
        case DatePeriodEnum.MONTH:
          shiftPeriodName = "MONTH";
          break;
        case DatePeriodEnum.QUARTER:
          shiftPeriodName = "QUARTER";
          break;
        case DatePeriodEnum.YEAR:
          shiftPeriodName = "YEAR";
          break;
      }
      // Create a custom name based on selected values
      const customName = `LAST ${numberOfPeriods} ${shiftPeriodName} AVERAGE`;
      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: numberOfPeriods,
          aggregation_type: AggregationType.AVERAGE, // Fixed to AVERAGE
          growth_period: shiftPeriod,
        })
      };
    }

    // If it's a simple Last X benchmark, no modification needed
    if (isSimpleLastBenchmark) {
      // No configuration needed - use as-is with fixed values
    }

    // If it's a Generalized Growth Benchmark (old style), update its configuration
    if (isGeneralizedGrowthBenchmark && benchmarkToAdd.growth_benchmark) {
      // Create a custom name based on selected values
      let aggregationTypeName = "Average";
      switch (generalizedAggregationType) {
        case AggregationType.SUM: aggregationTypeName = "Sum"; break;
        case AggregationType.MIN: aggregationTypeName = "Min"; break;
        case AggregationType.MAX: aggregationTypeName = "Max"; break;
        case AggregationType.COUNT: aggregationTypeName = "Count"; break;
        case AggregationType.UNIQUE_COUNT: aggregationTypeName = "Unique Count"; break;
        default: aggregationTypeName = "Average";
      }
      
      const shiftPeriodName = shiftPeriod.toString().charAt(0) + shiftPeriod.toString().slice(1).toLowerCase();
      const customName = `${generalizedNumberOfPeriods} ${shiftPeriodName}(s) ago ${aggregationTypeName}`;
      
      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: generalizedNumberOfPeriods,
          aggregation_type: generalizedAggregationType,
          growth_period: shiftPeriod,
        })
      };
    }

    // If it's a N Period Ago benchmark, update its configuration
    if (isNPeriodAgo && benchmarkToAdd.growth_benchmark) {
      const shiftPeriodName = datePeriodEnumToJSON(shiftPeriod).charAt(0) + datePeriodEnumToJSON(shiftPeriod).slice(1).toLowerCase();
      const customName = `${generalizedNumberOfPeriods} ${shiftPeriodName}${generalizedNumberOfPeriods > 1 ? "s" : ""} Ago`;
      
      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: generalizedNumberOfPeriods,
          growth_period: shiftPeriod,
          // No aggregation_type 
        })
      };
    }

    // Update selectedBenchmarks if not already included
    const updatedBenchmarks = benchmarkExists 
      ? selectedBenchmarks.map(b => 
          b.header?.id === benchmarkToAdd.header?.id ? benchmarkToAdd : b
        )
      : [...selectedBenchmarks, benchmarkToAdd];

    // NEW APPROACH: Create ColumnHashProto entries for benchmark + metrics or benchmark + measures(no analysis column types)
    const newBenchmarkColumnHashes: ColumnHashProto[] = [];

    // Create column hashes for each selected metric with this benchmark
    selectedMetricsForBenchmark.forEach(metric => {
      if (metric.header?.id && benchmarkToAdd?.header?.id) {
        const columnHash: ColumnHashProto = {
          actuals_id: metric.header.id,
          benchmark_id: benchmarkToAdd.header.id
        };
        // Set column_name only if a custom name is provided
        const customName = customNamesByActualsId[metric.header.id];
        if (customName) columnHash.column_name = customName;
        newBenchmarkColumnHashes.push(columnHash);
      }
    });


    // Also create column hashes for measure columns (from selectedColumns) with this benchmark
    selectedMeasureColumns.forEach(column => {
      if (column.column_id && benchmarkToAdd?.header?.id) {
        const columnHash: ColumnHashProto = {
          actuals_id: column.column_id,
          benchmark_id: benchmarkToAdd.header.id
        };
        // Set column_name only if a custom name is provided
        const customName = customNamesByActualsId[column.column_id];
        if (customName) columnHash.column_name = customName;
        newBenchmarkColumnHashes.push(columnHash);
      }
    });

    // Combine with existing benchmark column hashes, removing any duplicates
    const updatedBenchmarkColumnHashes = [...benchmarkColumnHashes];
    newBenchmarkColumnHashes?.forEach(newColumn => {
      // Check if this exact combination already exists
      const exists = updatedBenchmarkColumnHashes.some(col =>
        col.actuals_id === newColumn.actuals_id &&
        col.benchmark_id === newColumn.benchmark_id
      );
      
      if (!exists) {
        updatedBenchmarkColumnHashes.push(newColumn);
      }
    });

    // Update state
    setSelectedBenchmarks(updatedBenchmarks);
    setBenchmarkColumnHashes(updatedBenchmarkColumnHashes);
    
    // Reset current selection and custom names
    setCurrentSelection(null);
    setSelectedMetricsForBenchmark([]);
    setCustomNamesByActualsId({});
  };

  // Remove a benchmark and its calculations
  const handleRemoveBenchmarkColumn = (benchmarkId: string, actualsId?: string) => {
    const updated = benchmarkColumnHashes.filter(h => !(h.benchmark_id === benchmarkId && h.actuals_id === actualsId));
    setBenchmarkColumnHashes(updated);
  };

  // Handle editing a benchmark name
  const handleEditName = (benchmarkId: string, actualsId: string, newName: string) => {
    // Update the name in the benchmark column hashes
    const updatedBenchmarkColumnHashes = benchmarkColumnHashes.map(columnHash => {
      if (columnHash.benchmark_id === benchmarkId && columnHash.actuals_id === actualsId) {
        return {
          ...columnHash,
          column_name: newName || undefined // Use undefined instead of empty string
        };
      }
      return columnHash;
    });
    // Update state
    setBenchmarkColumnHashes(updatedBenchmarkColumnHashes);
  };

  // New helpers for per-hash edit/propagate/remove
  const handleStartEdit = (benchmarkId: string, actualsId: string | undefined, currentName: string) => {
    const key = `${benchmarkId}::${actualsId || ""}`;
    setEditingKey(key);
    setEditingColumnName(currentName || "");
  };

  const handleSaveEditForKey = () => {
    if (!editingKey) return;
    const [bId, aId] = editingKey.split("::");
    if (bId && aId !== undefined) {
      handleEditName(bId, aId, editingColumnName);
    }
    setEditingKey("");
    setEditingColumnName("");
  };

  const handleCancelEditForKey = () => {
    setEditingKey("");
    setEditingColumnName("");
  };



  // Helper function to get benchmark name
  const getBenchmarkName = (benchmark: BenchmarkEntityProto | null): string => {
    return benchmark?.header?.name || "Unnamed Benchmark";
  };

  return (
    <Stack spacing={4}>
      {/* Benchmark Selection Dropdown */}
      <FormControl>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={availableBenchmarks.length === 0}
          >
            {currentSelection
              ? getBenchmarkName(currentSelection)
              : availableBenchmarks.length === 0
              ? "No benchmarks available"
              : "Select a benchmark"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {availableBenchmarks.map((benchmark, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleBenchmarkSelect(benchmark)}
                  _hover={{ bg: "purple.50" }}
                >
                  <Flex alignItems="center" width="100%" fontWeight="600">
                    {getBenchmarkName(benchmark)}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Last N Period Aggregation Configuration */}
      {isLastNPeriodAggregation && (
        <FormControl>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Box>
              <Text fontSize="sm" mb={1} fontWeight="600"># of Periods (N)</Text>
              <NumberInput 
                min={1} 
                value={numberOfPeriods} 
                onChange={(_, value) => setNumberOfPeriods(value)}
                size="sm"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Box>
          </Box>
        </FormControl>
      )}

      {/* Generalized Growth Benchmark Configuration */}
      {isGeneralizedGrowthBenchmark && (
        <FormControl>
          <FormLabel>Generalized Growth Benchmark Configuration</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Stack spacing={3}>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>Number of Periods</Text>
                  <NumberInput 
                    min={1} 
                    value={generalizedNumberOfPeriods} 
                    onChange={(_, value) => setGeneralizedNumberOfPeriods(value)}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
                <Box>
                  <Text fontSize="xs" mb={1}>Aggregation Type</Text>
                  <Select 
                    size="sm" 
                    value={generalizedAggregationType}
                    onChange={(e) => setGeneralizedAggregationType(parseInt(e.target.value, 10) as AggregationType)}
                  >
                    <option value={AggregationType.SUM}>Sum</option>
                    <option value={AggregationType.AVERAGE}>Average</option>
                    <option value={AggregationType.COUNT}>Count</option>
                    <option value={AggregationType.UNIQUE_COUNT}>Unique Count</option>
                    <option value={AggregationType.MIN}>Min</option>
                    <option value={AggregationType.MAX}>Max</option>
                  </Select>
                </Box>
              </HStack>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>Shift Period</Text>
                  <Select 
                    size="sm" 
                    value={shiftPeriod}
                    onChange={(e) => setShiftPeriod(parseInt(e.target.value, 10) as DatePeriodEnum)}
                  >
                    <option value={DatePeriodEnum.DAY}>Day</option>
                    <option value={DatePeriodEnum.WEEK}>Week</option>
                    <option value={DatePeriodEnum.MONTH}>Month</option>
                    <option value={DatePeriodEnum.QUARTER}>Quarter</option>
                    <option value={DatePeriodEnum.YEAR}>Year</option>
                  </Select>
                </Box>
              </HStack>
            </Stack>
          </Box>
        </FormControl>
      )}

      {/* N Period Ago Configuration (without aggregation) */}
      {isNPeriodAgo && (
        <FormControl>
          <FormLabel>N Period Ago Configuration</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Stack spacing={3}>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>Number of Periods</Text>
                  <NumberInput 
                    min={1} 
                    value={generalizedNumberOfPeriods} 
                    onChange={(_, value) => setGeneralizedNumberOfPeriods(value)}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
              </HStack>
              <Text fontSize="xs" color="gray.600" fontStyle="italic">
                This benchmark compares with data from exactly N {datePeriodEnumToJSON(shiftPeriod).toLowerCase()}s ago without any aggregation.
              </Text>
            </Stack>
          </Box>
        </FormControl>
      )}

      {/* Metric Selection */}
      <FormControl>
        <Text mb={1} fontWeight="semibold" color="purple.700" ml={1}>Select Metrics</Text>
        <Menu closeOnSelect={false}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={!currentNode?.selected_metrics || currentNode.selected_metrics.length === 0}
          >
            {selectedMetricsForBenchmark.length > 0
              ? `${selectedMetricsForBenchmark.length} metric(s) selected`
              : !currentNode?.selected_metrics || currentNode.selected_metrics.length === 0
              ? "No metrics available"
              : "Select metrics"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {/* Select All Option */}
              <MenuItem
                onClick={handleSelectAllMetrics}
                _hover={{ bg: "purple.50" }}
                fontWeight="700"
                closeOnSelect={false}
                borderBottom="1px solid"
                borderColor="gray.200"
                mb={1}
              >
                <Checkbox
                  isChecked={isAllMetricsSelected}
                  pointerEvents="none"
                  mr={2}
                  colorScheme="purple"
                  mb={0}
                />
                Select All
              </MenuItem>
              {currentNode?.selected_metrics?.map((metric, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleMetricToggleForBenchmark(metric)}
                  _hover={{ bg: "purple.50" }}
                  fontWeight="600"
                  closeOnSelect={false}
                >
                  <Checkbox
                    isChecked={selectedMetricsForBenchmark.some(m => m.header?.id === metric.header?.id)}
                    pointerEvents="none"
                    mr={2}
                    colorScheme="purple"
                    mb={0}
                  />
                  {metric.header?.name || "Unnamed Metric"}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>
      

      {/* Add Benchmark Button - visible when a benchmark and metrics/measures are selected */}
      {canAddBenchmark && (
        <FormControl>
          <Button
            colorScheme="purple"
            onClick={handleAddSelection}
            width="100%"
            bg="purple.700"
            fontWeight="700"
            _hover={{ bg: "purple.600" }}
          >
            Add Benchmark Analysis
          </Button>
        </FormControl>
      )}

      {/* Display selected benchmark analyses grouped by benchmark */}
      {benchmarkColumnHashes.length > 0 && (
        <Box mt={4}>
          <Text fontWeight="medium" mb={2} color="purple.700">
            Selected Benchmark Analyses:
          </Text>
          <Stack spacing={2}>
            {(() => {
              // Group column hashes by benchmark_id
              const groupedByBenchmark = benchmarkColumnHashes.reduce((acc, hash) => {
                const benchmarkId = hash.benchmark_id || "";
                if (!acc[benchmarkId]) {
                  acc[benchmarkId] = [];
                }
                acc[benchmarkId].push(hash);
                return acc;
              }, {} as Record<string, ColumnHashProto[]>);


              return Object.entries(groupedByBenchmark).flatMap(([benchmarkId, hashes]) => {
                const benchmark = currentNode?.selected_benchmarks?.find(b => b.header?.id === benchmarkId);
                const benchmarkName = benchmark?.header?.name || "Unknown Benchmark";

                return hashes.map((hash, idx) => {
                  const key = `${benchmarkId}::${hash.actuals_id || idx}`;

                  const metric = currentNode?.metrics?.find(m => m.header?.id === hash.actuals_id);
                  const measure = currentNode?.non_benchmarks?.find(c =>
                    c.column_id === hash.actuals_id && c.kg_column_header?.column_type === ColumnType.MEASURE
                  );

                  const actualsName = metric?.header?.name || measure?.kg_column_header?.name || "Unknown";
                  const displayName = (hash as any).column_name || actualsName;

                  return (
                    <Box
                      key={key}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      backgroundColor="gray.50"
                      borderColor="purple.700"
                    >
                        <Flex justifyContent="space-between" alignItems="center">
                          <Box flex="1" mr={2}>
                            {editingKey === key && 
                            (<>
                              <Input
                                value={editingColumnName}
                                onChange={(e) => setEditingColumnName(e.target.value)}
                                placeholder="Enter column name"
                                size="sm"
                                bg="white"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleSaveEditForKey();
                                  } else if (e.key === "Escape") {
                                    handleCancelEditForKey();
                                  }
                                }}
                              />
                              <HStack spacing={1}>
                                <IconButton
                                  aria-label="Save column name"
                                  icon={<CheckIcon />}
                                  size="xs"
                                  onClick={handleSaveEditForKey}
                                  variant="ghost"
                                  colorScheme="green"
                                />
                                <IconButton
                                  aria-label="Cancel edit"
                                  icon={<CloseIcon />}
                                  size="xs"
                                  onClick={handleCancelEditForKey}
                                  variant="ghost"
                                />
                              </HStack>
                              </>)
                            }
                            <Text fontSize="sm" fontWeight="bold" color="purple.800" noOfLines={1} title={displayName}>
                              {displayName}
                            </Text>
                            <Box>
                              <FormLabel mb={0} fontSize="xs" color="gray.600">Actuals</FormLabel>
                              <Box mb={1}>
                              <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                                {actualsName}
                              </Box>
                              </Box>
                              <FormLabel mb={0} fontSize="xs" color="gray.600">Benchmark</FormLabel>
                              <Box mb={1}>
                                <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                                  {benchmarkName}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                          <HStack spacing={1}>
                            <IconButton
                              aria-label="Edit column name"
                              icon={<Icon as={EditIcon} boxSize={4} color="purple.700" />}
                              size="xs"
                              onClick={() => handleStartEdit(benchmarkId, hash.actuals_id, (hash as any).column_name || displayName)}
                              variant="ghost"
                              colorScheme="purple"
                              _hover={{ bg: "purple.100" }}
                            />
                            <IconButton
                              aria-label="Propagate benchmark column"
                              icon={<Icon as={TbFilterBolt} boxSize={4} color="purple.700" />}
                              size="xs"
                              onClick={() => handlePropagateBenchmarkClick(benchmark, currentNode?.id || "", hash)}
                              variant="ghost"
                              colorScheme="purple"
                              _hover={{ bg: "purple.100" }}
                              isDisabled={!benchmark}
                            />
                            <IconButton
                              aria-label="Remove benchmark column"
                              icon={<Icon as={CloseIcon} boxSize={3} _hover={{boxSize:4}} />}
                              size="xs"
                              onClick={() => handleRemoveBenchmarkColumn(benchmarkId, hash.actuals_id)}
                              variant="ghost"
                              colorScheme="red"
                            />
                          </HStack>
                        </Flex>
                    </Box>
                  );
                });
              });
            })()}
          </Stack>
        </Box>
      )}

      {/* Propagation Modal */}
      <PropagationModal
        isOpen={propagationModal.isOpen}
        onClose={handleClosePropagation}
        item={propagationModal.item}
        availableNodes={propagationModal.availableNodes}
        shouldOverwrite={propagationModal.shouldOverwrite}
        onToggleOverwrite={handlePropagationOverwriteToggle}
        onToggleNode={handlePropagationNodeToggle}
        onConfirm={handleConfirmPropagation}
      />
    </Stack>
  );
};

export default Benchmarks;  